import type { AcquireAttempt, MediaProgress, SkipRange, SubtitleTrack } from './media.js';
import type { MemberSyncView, RoomMemberView, RoomQueueItemView, RoomSnapshot } from './rooms.js';

/**
 * Nazwy eventów WS w jednym miejscu. Serwer i klient importują te same stałe,
 * więc literówka jest błędem kompilacji, a nie ciszą w runtime.
 */
export const ServerEvent = {
  RoomState: 'room:state',
  RoomSettings: 'room:settings',
  RoomExpiring: 'room:expiring',
  RoomExpired: 'room:expired',
  RoomExtended: 'room:extended',
  RoomKick: 'room:kick',
  RoomError: 'room:error',

  UserList: 'user:list',
  /**
   * Zgodność uczestników z odtwarzaczem prowadzącego.
   *
   * Osobne zdarzenie, a nie pole w `user:list`: lista składu zmienia się przy
   * wejściu i wyjściu, a odchylenie co dwie sekundy — rozsyłanie całego składu
   * w tym tempie byłoby setkami bajtów bez treści.
   */
  UserSync: 'user:sync',
  UserJoin: 'user:join',
  UserLeave: 'user:leave',
  UserCount: 'user:count',
  UserUpdate: 'user:update',

  LeaderChange: 'leader:change',

  MediaChange: 'media:change',
  MediaUpdate: 'media:update',
  MediaCountdown: 'media:countdown',
  MediaAdvanceCountdown: 'media:advance-countdown',
  /** Postęp pobierania i przetwarzania — widzą go WSZYSCY w pokoju. */
  MediaProgress: 'media:progress',
  MediaReady: 'media:ready',
  MediaFailed: 'media:failed',

  QueueState: 'queue:state',

  ChatMessage: 'chat:message',
  ChatClear: 'chat:clear',
  ReactionEmit: 'reaction:emit',

  TimePong: 'time:pong',

  CatalogueEpisodesProgress: 'catalogue:episodes-progress',
  SkipRanges: 'skip:ranges',
  SubsReady: 'subs:ready',
} as const;

export const ClientEvent = {
  RoomJoin: 'room:join',
  RoomLeave: 'room:leave',

  MediaUpdate: 'media:update',
  MediaSkip: 'media:skip',
  MediaEnded: 'media:ended',
  /**
   * Takt zgłoszeniowy: „jestem tu, jestem w tym miejscu materiału".
   *
   * Wysyła KAŻDY, nie tylko prowadzący — inaczej nie da się powiedzieć, czy
   * gość jest zgodny, a rozjechany widz sam o tym nie wie.
   */
  MediaHeartbeat: 'media:heartbeat',
  MediaRetry: 'media:retry',

  QueueAdd: 'queue:add',
  QueueRemove: 'queue:remove',
  QueueReorder: 'queue:reorder',
  QueueClear: 'queue:clear',

  LeaderClaim: 'leader:claim',
  LeaderSet: 'leader:set',

  ChatSend: 'chat:send',
  ReactionSend: 'reaction:send',

  UserKick: 'user:kick',
  UserBan: 'user:ban',
  UserMute: 'user:mute',

  TimePing: 'time:ping',
} as const;

export type ServerEventName = (typeof ServerEvent)[keyof typeof ServerEvent];
export type ClientEventName = (typeof ClientEvent)[keyof typeof ClientEvent];

/** Stan odtwarzania rozsyłany do followerów. */
export interface MediaUpdatePayload {
  time: number;
  paused: boolean;
  /** Stempel serwera w chwili emisji — podstawa ekstrapolacji u followera. */
  emittedAt: number;
  source: 'leader' | 'server';
}

export interface MediaChangePayload {
  queueItem: RoomQueueItemView;
  time: number;
  paused: boolean;
  emittedAt: number | null;
  /** Zegar serwera, żeby spóźniony klient mógł od razu policzyć offset. */
  serverNow: number;
  subtitles: SubtitleTrack[];
  skipRanges: SkipRange[];
}

export interface MediaFailedPayload {
  mediaFileId: string;
  attempts: AcquireAttempt[];
  /** Czy jakikolwiek host pozostał do wypróbowania (decyduje o przycisku ponowienia). */
  retryable: boolean;
}

export interface AdvanceCountdownPayload {
  secondsLeft: number;
  totalSeconds: number;
  nextItem: RoomQueueItemView | null;
}

export interface TimePongPayload {
  clientNow: number;
  serverNow: number;
}

/**
 * Mapa payloadów. Używana przez typowany wrapper socket.io po obu stronach —
 * emit z nieprawidłowym payloadem nie skompiluje się.
 */
export interface ServerToClientEvents {
  [ServerEvent.RoomState]: (p: RoomSnapshot) => void;
  [ServerEvent.RoomSettings]: (p: Record<string, unknown>) => void;
  [ServerEvent.RoomExpiring]: (p: { expiresAt: string }) => void;
  [ServerEvent.RoomExpired]: (p: { reason: string }) => void;
  [ServerEvent.RoomExtended]: (p: { expiresAt: string }) => void;
  [ServerEvent.RoomKick]: (p: { reason: string }) => void;
  [ServerEvent.RoomError]: (p: { code: string; message: string }) => void;

  [ServerEvent.UserList]: (p: RoomMemberView[]) => void;
  [ServerEvent.UserSync]: (p: { members: MemberSyncView[] }) => void;
  [ServerEvent.UserJoin]: (p: RoomMemberView) => void;
  [ServerEvent.UserLeave]: (p: { userKey: string }) => void;
  [ServerEvent.UserCount]: (p: { count: number }) => void;
  [ServerEvent.UserUpdate]: (p: RoomMemberView) => void;

  [ServerEvent.LeaderChange]: (p: { leaderUserKey: string | null }) => void;

  [ServerEvent.MediaChange]: (p: MediaChangePayload) => void;
  [ServerEvent.MediaUpdate]: (p: MediaUpdatePayload) => void;
  [ServerEvent.MediaCountdown]: (p: { secondsLeft: number }) => void;
  [ServerEvent.MediaAdvanceCountdown]: (p: AdvanceCountdownPayload) => void;
  [ServerEvent.MediaProgress]: (p: MediaProgress) => void;
  [ServerEvent.MediaReady]: (p: { mediaFileId: string }) => void;
  [ServerEvent.MediaFailed]: (p: MediaFailedPayload) => void;

  [ServerEvent.QueueState]: (p: RoomQueueItemView[]) => void;

  [ServerEvent.ChatMessage]: (p: ChatMessageView) => void;
  [ServerEvent.ChatClear]: (p: Record<string, never>) => void;
  [ServerEvent.ReactionEmit]: (p: { userKey: string; emote: string; atSec: number }) => void;

  [ServerEvent.TimePong]: (p: TimePongPayload) => void;

  [ServerEvent.CatalogueEpisodesProgress]: (p: {
    titleId: string;
    sourceId: string;
    done: number;
    total: number;
    found: number[];
  }) => void;
  [ServerEvent.SkipRanges]: (p: { mediaFileId: string; ranges: SkipRange[] }) => void;
  [ServerEvent.SubsReady]: (p: { mediaFileId: string; tracks: SubtitleTrack[] }) => void;
}

export interface ClientToServerEvents {
  [ClientEvent.RoomJoin]: (p: { slug: string; nickname?: string; password?: string }) => void;
  [ClientEvent.RoomLeave]: (p: { slug: string }) => void;

  [ClientEvent.MediaUpdate]: (p: { slug: string; time: number; paused: boolean }) => void;
  [ClientEvent.MediaSkip]: (p: { slug: string; uid?: string }) => void;
  [ClientEvent.MediaEnded]: (p: { slug: string; uid: string }) => void;
  [ClientEvent.MediaRetry]: (p: { slug: string; mediaFileId: string; host?: string }) => void;

  [ClientEvent.QueueAdd]: (p: {
    slug: string;
    kind: 'episode' | 'upload';
    titleId?: string;
    episode?: number;
    providerTitleId?: string;
    mediaFileId?: string;
    next?: boolean;
  }) => void;
  [ClientEvent.QueueRemove]: (p: { slug: string; uid: string }) => void;
  [ClientEvent.QueueReorder]: (p: { slug: string; uids: string[] }) => void;
  [ClientEvent.QueueClear]: (p: { slug: string }) => void;

  [ClientEvent.LeaderClaim]: (p: { slug: string }) => void;
  [ClientEvent.LeaderSet]: (p: { slug: string; userKey: string }) => void;

  [ClientEvent.ChatSend]: (p: { slug: string; content: string }) => void;
  [ClientEvent.ReactionSend]: (p: { slug: string; emote: string; atSec: number }) => void;

  [ClientEvent.UserKick]: (p: { slug: string; userKey: string; reason?: string }) => void;
  [ClientEvent.UserBan]: (p: { slug: string; userKey: string; reason?: string }) => void;
  [ClientEvent.UserMute]: (p: { slug: string; userKey: string; muted: boolean }) => void;

  [ClientEvent.TimePing]: (p: { clientNow: number }) => void;
}

export interface ChatMessageView {
  id: string;
  userKey: string;
  displayName: string;
  isGuest: boolean;
  content: string;
  type: 'text' | 'system' | 'emote';
  createdAt: string;
}
